import React, { useState, useRef, useEffect } from "react";
import {
  FileText,
  Loader2,
  ScrollText,
  Briefcase,
  Gavel,
  Copy,
  Download,
  ArrowRight,
  X,
  CheckCircle2,
  Sparkles,
  Share2,
  FileDown,
} from "lucide-react";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import ShareDialog from "@/components/ShareDialog";
import { downloadAsPDF } from "@/lib/pdf";
import { useActiveProject } from "@/lib/ActiveProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const KINDS = [
  {
    id: "mom",
    title: "MOM Generator",
    desc: "Convert raw notes into structured Minutes of Meeting with action items, owners and deadlines.",
    icon: FileText,
    fields: [
      { k: "meeting_title", l: "Meeting title", default: "Project review — Solar PV 500 MW" },
      { k: "attendees", l: "Attendees (comma separated)", default: "PM, Engineer, Contractor PD, QA" },
      {
        k: "discussion_notes",
        l: "Discussion notes",
        textarea: true,
        default:
          "Discussed inverter foundation drawing delay; PM raised module shipment risk; QA confirmed structure inspection backlog. Decided: contractor to issue 20.2 notice; engineer to release IFC by next Friday; QA to add 2 inspectors.",
      },
    ],
  },
  {
    id: "notice",
    title: "Notice Generator",
    desc: "Draft a FIDIC-compliant contractual notice ready for issue under the relevant sub-clause.",
    icon: ScrollText,
    fields: [
      { k: "project_name", l: "Project", default: "" },
      { k: "clause_reference", l: "Clause reference (optional)", default: "20.2.1" },
      {
        k: "event_description",
        l: "Event description",
        textarea: true,
        default:
          "IFC drawings for inverter foundations released 56 days late by the Engineer, causing 35 days delay to the critical path.",
      },
    ],
  },
  {
    id: "proposal",
    title: "Proposal Generator",
    desc: "Generate a winning EPC technical–commercial proposal in seconds.",
    icon: Briefcase,
    fields: [
      { k: "client", l: "Client / Tender", default: "Saudi Power Procurement Co." },
      { k: "project_scope", l: "Scope summary", default: "EPC for 800 MW solar PV plant in NEOM region" },
      {
        k: "win_themes",
        l: "Win themes",
        textarea: true,
        default:
          "Local content > 35%, IRR-positive financing model, 18-month aggressive schedule, in-house O&M.",
      },
    ],
  },
  {
    id: "claim",
    title: "Claim Draft",
    desc: "Produce a structured claim document with facts, contractual basis, quantum and substantiation.",
    icon: Gavel,
    fields: [
      { k: "project_name", l: "Project", default: "" },
      { k: "clause_reference", l: "FIDIC clause", default: "8.5 / 20.2" },
      {
        k: "event",
        l: "Event & impact",
        textarea: true,
        default:
          "Late IFC drawings for inverter foundations caused 35-day critical-path delay and additional prolongation cost of USD 3.2 mn.",
      },
      { k: "relief_sought", l: "Relief sought", default: "EOT 35 days + USD 3.2 mn cost" },
    ],
  },
];

export default function Automation() {
  const { project } = useActiveProject();
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const outputRef = useRef(null);

  const launch = (k) => {
    setActive(k);
    const initial = {};
    // Fill from active project context first, then fall back to the card's default
    const pctx = project || {};
    const projectName = pctx.name || "";
    const notes = pctx.notes || pctx.description || "";
    const fidic = (pctx.contract_type || "").replace(/^FIDIC\s+/i, "");
    k.fields.forEach((f) => {
      if (project && f.k === "project_name" && projectName) initial[f.k] = projectName;
      else if (project && f.k === "client" && pctx.client) initial[f.k] = pctx.client;
      else if (project && f.k === "meeting_title" && projectName)
        initial[f.k] = `Project review — ${projectName}`;
      else if (project && (f.k === "event_description" || f.k === "event" || f.k === "discussion_notes" || f.k === "project_scope") && notes)
        initial[f.k] = notes;
      else if (project && f.k === "clause_reference" && fidic) initial[f.k] = fidic;
      else initial[f.k] = f.default || "";
    });
    setForm(initial);
    setOpen(true);
  };

  const generate = async () => {
    if (!active) return;
    setOpen(false);
    setLoading(true);
    setOutput({ kind: active.id, title: active.title, body: "" });
    try {
      const { data } = await api.post("/automation/generate", {
        kind: active.id,
        payload: form,
      });
      setOutput({ kind: active.id, title: active.title, body: data.document });
      toast.success(`${active.title} ready`);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Generation failed. Please retry in a moment."
      );
      setOutput(null);
    } finally {
      setLoading(false);
    }
  };

  // scroll to output when it appears
  useEffect(() => {
    if ((loading || output) && outputRef.current) {
      outputRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, output]);

  return (
    <div className="space-y-6" data-testid="automation-page">
      <SectionHeader
        overline="Module 04 · Ready for official contractual issuance"
        title="Automation Engine"
        description="Four professional generators that turn rough notes into board-ready documents."
        testid="automation-header"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {KINDS.map((k) => {
          const isActive = output?.kind === k.id || (loading && active?.id === k.id);
          return (
            <button
              key={k.id}
              onClick={() => launch(k)}
              data-testid={`automation-card-${k.id}`}
              className={`text-left bg-slate-900/60 border ${
                isActive
                  ? "border-cyan-500/70"
                  : "border-slate-800 hover:border-cyan-500/60"
              } hover:bg-slate-900 transition-colors rounded-sm p-5 group relative overflow-hidden`}
            >
              <div className="absolute top-0 right-0 w-24 h-1 scanline opacity-50" />
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 grid place-items-center bg-slate-950 border border-slate-800 group-hover:border-cyan-500/60 rounded-sm">
                  <k.icon className="h-5 w-5 text-cyan-400" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold text-slate-50">
                      {k.title}
                    </h3>
                    {isActive && output?.body ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">
                    {k.desc}
                  </p>
                  <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600 group-hover:text-cyan-400 transition-colors">
                    {isActive && output?.body
                      ? "Output ready ↓"
                      : "Click to generate →"}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Inline output panel */}
      {(loading || output) && (
        <div ref={outputRef} data-testid="automation-output-panel">
          <OutputPanel
            loading={loading}
            output={output}
            onShare={() => setShareOpen(true)}
            onClose={() => {
              setOutput(null);
              setActive(null);
            }}
          />
        </div>
      )}

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        kind={output?.kind || "document"}
        title={output?.title || "Automation Output"}
        body={output?.body || null}
        projectName=""
      />

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-slate-950 border-slate-800 text-slate-100 max-w-xl rounded-sm"
          data-testid="automation-form"
        >
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {active && <active.icon className="h-4 w-4 text-cyan-400" />}
              {active?.title}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Fill the inputs below — Claude will draft the document inline.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {active?.fields.map((f) => (
              <div key={f.k}>
                <Label className="text-xs text-slate-400">{f.l}</Label>
                {f.textarea ? (
                  <Textarea
                    rows={5}
                    value={form[f.k] || ""}
                    onChange={(e) =>
                      setForm({ ...form, [f.k]: e.target.value })
                    }
                    data-testid={`auto-input-${f.k}`}
                    className="mt-1.5 bg-slate-900 border-slate-800 rounded-sm text-slate-100"
                  />
                ) : (
                  <Input
                    value={form[f.k] || ""}
                    onChange={(e) =>
                      setForm({ ...form, [f.k]: e.target.value })
                    }
                    data-testid={`auto-input-${f.k}`}
                    className="mt-1.5 bg-slate-900 border-slate-800 rounded-sm text-slate-100"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <Button
              onClick={generate}
              data-testid="automation-generate-btn"
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Generate Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OutputPanel({ loading, output, onClose, onShare }) {
  const body = output?.body || "";
  const copy = () => {
    navigator.clipboard.writeText(body);
    toast.success("Copied to clipboard");
  };
  const download = () => {
    const blob = new Blob([body], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${output.kind}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const downloadPdf = () => {
    downloadAsPDF({
      title: output?.title || "Document",
      body,
      kind: output?.kind || "document",
    });
    toast.success("PDF downloaded");
  };
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-sm">
      <div className="flex items-center justify-between p-4 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-cyan-400" />
          <div>
            <div className="font-display text-base font-semibold text-slate-50">
              {output?.title} · Output
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
              {loading ? "Generating…" : "Ready for official issuance"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!loading && body && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onShare}
                data-testid="output-share-btn"
                className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-800 rounded-sm"
              >
                <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copy}
                data-testid="output-copy-btn"
                className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-800 rounded-sm"
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={download}
                data-testid="output-download-md-btn"
                className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-800 rounded-sm"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> .md
              </Button>
              <Button
                size="sm"
                onClick={downloadPdf}
                data-testid="output-download-btn"
                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
              >
                <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="output-close-btn"
            className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="p-5 max-h-[600px] overflow-y-auto scroll-clean">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-6 w-6 text-cyan-400 animate-spin mx-auto mb-3" />
            <div className="text-xs font-mono text-cyan-400 uppercase tracking-[0.25em]">
              Drafting document…
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Claude is composing a board-ready output. Typically 15–30 seconds.
            </div>
          </div>
        ) : body ? (
          <pre
            className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-slate-200"
            data-testid="automation-doc-body"
          >
            {body}
          </pre>
        ) : null}
      </div>
    </div>
  );
}
